import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, signal, viewChild } from '@angular/core';

const POS_KEY = 'pm-pinned-timer-pos';
const EDGE = 8;

/** Last spot the pill was dropped (per browser). Storage can be blocked, so every access is guarded. */
function readSavedPos(): { x: number; y: number } | null {
  try {
    const raw = pmStorageGet(POS_KEY);
    const p = raw ? (JSON.parse(raw) as { x: unknown; y: unknown }) : null;
    return p && typeof p.x === 'number' && typeof p.y === 'number' ? { x: p.x, y: p.y } : null;
  } catch {
    return null;
  }
}

function savePos(p: { x: number; y: number }): void {
  try {
    pmStorageSet(POS_KEY, JSON.stringify(p));
  } catch {
    /* storage blocked - the pill just starts at the top-right next time */
  }
}
import { Router } from '@angular/router';
import { PmWorkspaceTimeTrackerTimerService } from '../../../layout/pm-workspace/pm-workspace-time-tracker-timer.service';

import { pmStorageGet, pmStorageSet } from '../../../core/utils/pm-storage.util';
import { PmTooltipDirective } from '../pm-tooltip/pm-tooltip.directive';
/**
 * The Record Time stopwatch pinned to the bottom-left of the app (draggable), on every page. Shown only while the
 * timer is pinned and has started; click the time to go back to Record Time, × to unpin, and
 * (while paused) the reset arrow to clear it.
 */
@Component({
  selector: 'pm-pinned-timer',
  imports: [PmTooltipDirective],
  standalone: true,
  template: `
    @if (visible()) {
    <div #pill class="ppt" [class.ppt--on]="timer.running()" [class.ppt--dragging]="dragging()" role="status"
      aria-label="Pinned timer - drag to move" [style.left.px]="pos()?.x" [style.top.px]="pos()?.y"
      [style.right]="pos() ? 'auto' : null" [style.bottom]="pos() ? 'auto' : null" (pointerdown)="startDrag($event)">
      <span class="ppt__dot" aria-hidden="true"></span>
      <button type="button" class="ppt__time" pmTooltip="Open Record Time" (click)="openRecordTime()">
        {{ head() }}@for (s of [sec()]; track s) {<span class="ppt__sec">{{ s }}</span>}
      </button>
      @if (!timer.running()) {
      <button type="button" class="ppt__unpin" pmTooltip="Reset timer" aria-label="Reset timer" (click)="timer.reset()">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
      </button>
      }
      <button type="button" class="ppt__unpin" pmTooltip="Unpin timer" aria-label="Unpin timer" (click)="timer.togglePinned()">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
    }
  `,
  styles: `
    .ppt {
      position: fixed;
      /* Starts bottom-left: an empty corner on every page (top-right sat on the client bar's buttons,
         bottom-right is the FAB). Dragging moves it; the dropped spot is remembered. */
      bottom: 16px;
      left: 16px;
      z-index: 9500;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      height: 32px;
      padding: 0 4px 0 12px;
      border-radius: 999px;
      background: var(--ds-palette-white);
      box-shadow: 0 8px 24px rgba(46, 59, 98, 0.18), 0 1px 4px rgba(105, 75, 175, 0.18);
      font-family: 'Inter', sans-serif;
      animation: ppt-in 0.3s ease-out;
    }
    .ppt { cursor: grab; touch-action: none; user-select: none; }
    .ppt--dragging { cursor: grabbing; box-shadow: 0 14px 32px rgba(46, 59, 98, 0.26), 0 2px 6px rgba(105, 75, 175, 0.2); }
    .ppt__dot { width: 6px; height: 6px; border-radius: 50%; background: var(--ds-palette-slate-300); }
    .ppt--on .ppt__dot { background: var(--ds-palette-green-600); animation: ppt-pulse 1s ease-in-out infinite; }
    .ppt__time {
      display: inline-flex;
      overflow: hidden;
      padding: 0;
      border: 0;
      background: none;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      letter-spacing: 0.3px;
      color: var(--ds-palette-slate-600);
      cursor: pointer;
    }
    .ppt--on .ppt__time { color: var(--ds-palette-brand); }
    .ppt--on .ppt__sec { animation: ppt-tick 0.3s ease-out; }
    .ppt__unpin {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: rgba(105, 75, 175, 0.1);
      color: var(--ds-palette-brand);
      cursor: pointer;
    }
    .ppt__unpin:hover { background: rgba(105, 75, 175, 0.2); }
    .ppt__unpin svg { width: 11px; height: 11px; fill: none; stroke: currentColor; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
    @keyframes ppt-in { from { opacity: 0; transform: translateY(-8px) scale(0.96); } to { opacity: 1; transform: none; } }
    @keyframes ppt-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.7); } }
    @keyframes ppt-tick { from { opacity: 0; transform: translateY(60%); } to { opacity: 1; transform: none; } }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PmPinnedTimerComponent {
  readonly timer = inject(PmWorkspaceTimeTrackerTimerService);
  private readonly router = inject(Router);

  /** Only once the timer has started: pinned and running, or paused with time on it. */
  readonly visible = computed(() => this.timer.pinned() && (this.timer.running() || this.timer.seconds() > 0));

  private readonly parts = computed(() => {
    const t = this.timer.seconds();
    const pad = (n: number) => String(n).padStart(2, '0');
    return [pad(Math.floor(t / 3600)), pad(Math.floor((t % 3600) / 60)), pad(t % 60)];
  });
  readonly head = computed(() => `${this.parts()[0]}:${this.parts()[1]}:`);
  readonly sec = computed(() => this.parts()[2]);

  /* ---- Drag: press anywhere on the pill except its buttons. ---- */
  private readonly pill = viewChild<ElementRef<HTMLElement>>('pill');
  /** Top-left in px once moved; null = the default top-right spot. */
  readonly pos = signal<{ x: number; y: number } | null>(readSavedPos());
  readonly dragging = signal(false);

  startDrag(ev: PointerEvent): void {
    const el = this.pill()?.nativeElement;
    if (!el || ev.button !== 0 || (ev.target as HTMLElement).closest('button')) return;
    ev.preventDefault();
    const rect = el.getBoundingClientRect();
    const dx = ev.clientX - rect.left;
    const dy = ev.clientY - rect.top;
    const clamp = (x: number, y: number) => ({
      x: Math.min(Math.max(EDGE, x), window.innerWidth - rect.width - EDGE),
      y: Math.min(Math.max(EDGE, y), window.innerHeight - rect.height - EDGE)
    });
    el.setPointerCapture(ev.pointerId);
    this.dragging.set(true);

    const move = (e: PointerEvent) => this.pos.set(clamp(e.clientX - dx, e.clientY - dy));
    const end = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', end);
      el.removeEventListener('pointercancel', end);
      this.dragging.set(false);
      const p = this.pos();
      if (p) savePos(p);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
  }

  openRecordTime(): void {
    void this.router.navigate(['/practice'], { queryParams: { tab: 'Record Time' } });
  }
}
